import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUUpLeft,
  Eraser,
  ArrowClockwise,
  FlipHorizontal,
  FlipVertical,
  SquaresFour,
  X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// every preset is runs and returns only — corners are always 90°
const PRESETS = [
  {
    key: "rectangle",
    label: "Rectangle",
    note: '96" x 25.5"',
    build: () => [
      [0, 0],
      [96, 0],
      [96, 25.5],
      [0, 25.5],
    ],
  },
  {
    key: "l-shape-left",
    label: "L-Shape Left",
    note: "L run + return",
    build: () => [
      [0, 0],
      [96, 0],
      [96, 25.5],
      [25.5, 25.5],
      [25.5, 67],
      [0, 67],
    ],
  },
  {
    key: "l-shape-right",
    label: "L-Shape Right",
    note: "L run + return",
    build: () => [
      [0, 0],
      [96, 0],
      [96, 67],
      [70.5, 67],
      [70.5, 25.5],
      [0, 25.5],
    ],
  },
  {
    key: "u-shape",
    label: "U-Shape",
    note: '108" horseshoe',
    build: () => [
      [0, 0],
      [108, 0],
      [108, 80],
      [82.5, 80],
      [82.5, 25.5],
      [25.5, 25.5],
      [25.5, 80],
      [0, 80],
    ],
  },
  {
    key: "island",
    label: "Island",
    note: '84" x 42"',
    build: () => [
      [0, 0],
      [84, 0],
      [84, 42],
      [0, 42],
    ],
  },
  {
    key: "peninsula",
    label: "Peninsula",
    note: '60" x 42"',
    build: () => [
      [0, 0],
      [60, 0],
      [60, 42],
      [34, 42],
      [34, 25.5],
      [0, 25.5],
    ],
  },
];

// polygon points are normalized 0..1 against the slab outline
export const polygonSqFt = (points, lengthIn, widthIn) => {
  if (!points || points.length < 3 || !lengthIn || !widthIn) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area += a.x * b.y - b.x * a.y;
  }
  const sqIn = (Math.abs(area) / 2) * lengthIn * widthIn;
  return Math.round((sqIn / 144) * 10) / 10;
};

const clamp01 = (n) => Math.min(1, Math.max(0, n));
const round = (n) => Math.round(n * 1000) / 1000;
const SAME_LINE = 0.2; // inches: corners this close share a line

const PresetPreview = ({ pts }) => {
  const w = Math.max(...pts.map((p) => p[0]));
  const h = Math.max(...pts.map((p) => p[1]));
  const pad = Math.max(w, h) * 0.1;
  return (
    <svg
      viewBox={`${-pad} ${-pad} ${w + pad * 2} ${h + pad * 2}`}
      preserveAspectRatio="xMidYMid meet"
      className="h-12 w-full"
      aria-hidden="true"
    >
      <polygon
        points={pts.map((p) => p.join(",")).join(" ")}
        fill="rgba(109,93,246,0.2)"
        stroke="#8b83f7"
        strokeWidth={Math.max(w, h) / 45}
      />
    </svg>
  );
};

export const SlabTracer = ({
  points,
  onChange,
  lengthIn,
  widthIn,
  readOnly = false,
}) => {
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const [mode, setMode] = useState("trace");
  const [snap, setSnap] = useState(true);
  const [editing, setEditing] = useState(null);
  const [boxW, setBoxW] = useState(600);

  // rendered width drives label collision spacing (labels are fixed-size pixels)
  useEffect(() => {
    const el = svgRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) =>
      setBoxW(entry.contentRect.width || 600),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const L = Number(lengthIn) || 0;
  const W = Number(widthIn) || 0;
  const VW = 600;
  const VH = Math.max(120, Math.min(600, Math.round(VW * (L ? W / L : 0.5))));
  // 10% breathing room around the slab so edge labels and handles never sit on the border
  const M = Math.round(Math.max(VW, VH) * 0.1);
  const TW = VW + 2 * M;
  const TH = VH + 2 * M;
  const sqft = useMemo(() => polygonSqFt(points, L, W), [points, L, W]);
  const fullSqft = L && W ? Math.round(((L * W) / 144) * 10) / 10 : 0;

  const inches = useMemo(
    () => points.map((p) => ({ x: p.x * L, y: p.y * W })),
    [points, L, W],
  );
  const toNorm = (p) => ({
    x: round(clamp01(p.x / (L || 1))),
    y: round(clamp01(p.y / (W || 1))),
  });
  const push = (pts) => onChange(pts.map(toNorm));

  const rel = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const vx = ((e.clientX - rect.left) / rect.width) * TW - M;
    const vy = ((e.clientY - rect.top) / rect.height) * TH - M;
    return { x: clamp01(vx / VW) * L, y: clamp01(vy / VH) * W };
  };

  const edgeInfo = (i) => {
    const a = inches[i];
    const b = inches[(i + 1) % inches.length];
    const horizontal = Math.abs(b.y - a.y) <= Math.abs(b.x - a.x);
    const len = horizontal ? Math.abs(b.x - a.x) : Math.abs(b.y - a.y);
    return { a, b, horizontal, len: Math.round(len * 10) / 10 };
  };

  // move a whole run of corners along one axis — that is what keeps every corner square
  const moveLine = (base, axis, coord, delta) => {
    const max = axis === "x" ? L : W;
    const d = Math.min(max - coord, Math.max(-coord, delta));
    return base.map((p) =>
      Math.abs(p[axis] - coord) < SAME_LINE ? { ...p, [axis]: p[axis] + d } : p,
    );
  };

  const applyLength = (i, value) => {
    const target = Number(value);
    const { a, b, horizontal, len } = edgeInfo(i);
    setEditing(null);
    if (!target || target <= 0 || !len) return;
    const axis = horizontal ? "x" : "y";
    const dir = b[axis] > a[axis] ? 1 : -1;
    push(moveLine(inches, axis, b[axis], (target - len) * dir));
  };

  const handleClick = (e) => {
    if (readOnly || mode !== "trace") return;
    const p = rel(e);
    const last = inches[inches.length - 1];
    let next = p;
    if (last)
      next =
        Math.abs(p.x - last.x) >= Math.abs(p.y - last.y)
          ? { x: p.x, y: last.y }
          : { x: last.x, y: p.y };
    if (snap) next = { x: Math.round(next.x), y: Math.round(next.y) };
    onChange([...points, toNorm(next)]);
  };

  const startShapeDrag = (e) => {
    if (readOnly || mode !== "move" || points.length < 3) return;
    dragRef.current = { type: "shape", start: rel(e), origin: inches };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const startEdgeDrag = (e, i) => {
    if (readOnly || mode !== "adjust") return;
    e.stopPropagation();
    const { a, horizontal } = edgeInfo(i);
    dragRef.current = {
      type: "edge",
      start: rel(e),
      origin: inches,
      horizontal,
      coord: horizontal ? a.y : a.x,
    };
    svgRef.current.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const p = rel(e);

    if (drag.type === "edge") {
      const axis = drag.horizontal ? "y" : "x";
      let delta = p[axis] - drag.start[axis];
      if (snap) delta = Math.round(delta);
      push(moveLine(drag.origin, axis, drag.coord, delta));
      return;
    }

    let dx = p.x - drag.start.x;
    let dy = p.y - drag.start.y;
    if (snap) {
      dx = Math.round(dx);
      dy = Math.round(dy);
    }
    const xs = drag.origin.map((q) => q.x);
    const ys = drag.origin.map((q) => q.y);
    dx = Math.min(L - Math.max(...xs), Math.max(-Math.min(...xs), dx));
    dy = Math.min(W - Math.max(...ys), Math.max(-Math.min(...ys), dy));
    push(drag.origin.map((q) => ({ x: q.x + dx, y: q.y + dy })));
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const fitInside = (pts) => {
    const w = Math.max(...pts.map((p) => p.x));
    const h = Math.max(...pts.map((p) => p.y));
    const fit = Math.min(1, L / (w || 1), W / (h || 1));
    return pts.map((p) => ({ x: p.x * fit, y: p.y * fit }));
  };

  const drawPreset = (preset) => {
    setEditing(null);
    push(fitInside(preset.build().map(([x, y]) => ({ x, y }))));
  };

  const rotate90 = () => {
    if (points.length < 3) return;
    const maxY = Math.max(...inches.map((p) => p.y));
    setEditing(null);
    push(fitInside(inches.map((p) => ({ x: maxY - p.y, y: p.x }))));
  };

  const flip = (axis) => {
    if (points.length < 3) return;
    const vals = inches.map((p) => p[axis]);
    const mid = (Math.min(...vals) + Math.max(...vals)) / 2;
    setEditing(null);
    push(inches.map((p) => ({ ...p, [axis]: 2 * mid - p[axis] })));
  };

  const cx = inches.length
    ? inches.reduce((s, p) => s + p.x, 0) / inches.length
    : 0;
  const cy = inches.length
    ? inches.reduce((s, p) => s + p.y, 0) / inches.length
    : 0;

  const labels = useMemo(() => {
    if (!(points.length > 2 && L && W)) return [];
    const raw = inches
      .map((_, i) => {
        const { a, b, horizontal, len } = edgeInfo(i);
        if (len < 1) return null;
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        // labels sit just off their line, out in the margin for the outer edges
        const off = M * 0.55;
        const vx = (mx / L) * VW + (horizontal ? 0 : mx > cx ? off : -off);
        const vy = (my / W) * VH + (horizontal ? (my > cy ? off : -off) : 0);
        const pct = (v, total) => Math.min(98, Math.max(2, ((v + M) / total) * 100));
        return { i, len, horizontal, left: pct(vx, TW), top: pct(vy, TH) };
      })
      .filter(Boolean);
    // keep labels from stacking on small screens: slide along their own line
    const boxH = (boxW * TH) / TW;
    const pxX = (p) => (p / 100) * boxW;
    const pxY = (p) => (p / 100) * boxH;
    const placed = [];
    raw.forEach((lab) => {
      let tries = 0;
      const hits = () =>
        placed.some(
          (o) =>
            Math.abs(pxX(o.left - lab.left)) < 68 &&
            Math.abs(pxY(o.top - lab.top)) < 38,
        );
      while (tries < 4 && hits()) {
        if (lab.horizontal) lab.left = Math.min(98, lab.left + (70 / boxW) * 100);
        else lab.top = Math.min(98, lab.top + (40 / boxH) * 100);
        tries += 1;
      }
      placed.push(lab);
    });
    return placed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, inches, L, W, M, TW, TH, VW, VH, boxW, cx, cy]);

  const hint = readOnly
    ? "Usable field"
    : mode === "move"
      ? "Drag the shape to move it on the slab"
      : mode === "adjust"
        ? "Drag a line in or out, or tap a size to type it"
        : "Tap the slab corner to corner — lines snap square";

  return (
    <div className="select-none space-y-3">
      {!readOnly && (
        <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            <SquaresFour size={12} weight="bold" className="text-indigo-300" />
            Predefined layouts
          </div>
          <div
            className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3"
            data-testid="tracer-presets"
          >
            {PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                data-testid={`preset-${preset.key}`}
                onClick={() => drawPreset(preset)}
                className="group rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-left transition-colors duration-150 hover:border-indigo-400/60 hover:bg-zinc-800"
              >
                <PresetPreview pts={preset.build()} />
                <div className="mt-2 text-xs font-bold text-zinc-100">
                  {preset.label}
                </div>
                <div className="font-mono-data text-[10px] text-indigo-300">
                  {preset.note}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          {hint}
        </div>
        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            <div className="flex overflow-hidden rounded-md border border-zinc-700">
              {[
                ["trace", "Trace"],
                ["adjust", "Adjust"],
                ["move", "Move"],
              ].map(([m, label]) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`tracer-mode-${m}`}
                  onClick={() => {
                    setMode(m);
                    setEditing(null);
                  }}
                  className={`h-10 px-4 text-xs font-semibold uppercase tracking-wider transition-colors duration-150 ${
                    mode === m
                      ? "bg-indigo-500 text-white"
                      : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="tracer-snap-toggle"
              onClick={() => setSnap(!snap)}
              title="Snap corners to whole inches"
              className={`h-10 rounded-md border px-4 text-xs font-semibold uppercase tracking-wider transition-colors duration-150 ${
                snap
                  ? "border-indigo-500 bg-indigo-500 text-white"
                  : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
              }`}
            >
              Snap 1"
            </button>
            <Button
              type="button"
              data-testid="tracer-rotate-90"
              variant="outline"
              onClick={rotate90}
              disabled={points.length < 3}
              title="Turn the shape 90°"
              className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-wider hover:bg-zinc-800"
            >
              <ArrowClockwise size={15} weight="bold" className="mr-1.5" /> 90°
            </Button>
            <Button
              type="button"
              data-testid="tracer-flip-h"
              variant="outline"
              onClick={() => flip("x")}
              disabled={points.length < 3}
              title="Flip left to right"
              className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-wider hover:bg-zinc-800"
            >
              <FlipHorizontal size={15} weight="bold" />
            </Button>
            <Button
              type="button"
              data-testid="tracer-flip-v"
              variant="outline"
              onClick={() => flip("y")}
              disabled={points.length < 3}
              title="Flip top to bottom"
              className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-wider hover:bg-zinc-800"
            >
              <FlipVertical size={15} weight="bold" />
            </Button>
            <Button
              type="button"
              data-testid="tracer-undo"
              variant="outline"
              onClick={() => onChange(points.slice(0, -1))}
              disabled={!points.length}
              className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-wider hover:bg-zinc-800"
            >
              <ArrowUUpLeft size={15} weight="bold" className="mr-2" /> Undo
            </Button>
            <Button
              type="button"
              data-testid="tracer-clear"
              variant="outline"
              onClick={() => {
                setEditing(null);
                onChange([]);
              }}
              disabled={!points.length}
              className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-wider hover:bg-zinc-800"
            >
              <Eraser size={15} weight="bold" className="mr-2" /> Clear
            </Button>
          </div>
        )}
      </div>

      <div className="relative">
        <svg
          ref={svgRef}
          data-testid="slab-tracer"
          viewBox={`${-M} ${-M} ${TW} ${TH}`}
          onClick={handleClick}
          onPointerDown={startShapeDrag}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className={`w-full select-none rounded-md border border-zinc-700 bg-zinc-950 ${
            readOnly
              ? ""
              : mode === "move"
                ? "cursor-move"
                : mode === "adjust"
                  ? "cursor-pointer"
                  : "cursor-crosshair"
          }`}
          style={{ touchAction: "none" }}
        >
          <defs>
            <pattern
              id="slabgrid"
              width="30"
              height="30"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M30 0H0V30"
                fill="none"
                stroke="#27272a"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect x="0" y="0" width={VW} height={VH} fill="url(#slabgrid)" />
          <rect
            x="1"
            y="1"
            width={VW - 2}
            height={VH - 2}
            fill="none"
            stroke="#52525b"
            strokeWidth="2.5"
          />
          {points.length > 1 && (
            <polygon
              points={points.map((p) => `${p.x * VW},${p.y * VH}`).join(" ")}
              fill="rgba(109,93,246,0.25)"
              stroke="#6d5df6"
              strokeWidth="3.5"
              strokeLinejoin="round"
            />
          )}
          {!readOnly &&
            mode === "adjust" &&
            points.length > 1 &&
            points.map((p, i) => {
              const q = points[(i + 1) % points.length];
              return (
                <line
                  key={`edge-${i}`}
                  data-testid={`tracer-edge-${i}`}
                  x1={p.x * VW}
                  y1={p.y * VH}
                  x2={q.x * VW}
                  y2={q.y * VH}
                  stroke="transparent"
                  strokeWidth="26"
                  strokeLinecap="round"
                  className="cursor-move"
                  onPointerDown={(e) => startEdgeDrag(e, i)}
                />
              );
            })}
          {points.map((p, i) => (
            <circle
              key={i}
              data-testid={`tracer-point-${i}`}
              cx={p.x * VW}
              cy={p.y * VH}
              r="8"
              fill="#6d5df6"
              stroke="#000"
              strokeWidth="2"
            />
          ))}
        </svg>

        {labels.map((lab) =>
          editing?.index === lab.i ? (
            <div
              key={lab.i}
              className="absolute z-10 flex items-center gap-1.5 rounded-lg border border-indigo-400 bg-zinc-950 p-1.5 shadow-xl"
              style={{
                left: `${lab.left}%`,
                top: `${lab.top}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <Input
                autoFocus
                data-testid={`tracer-label-input-${lab.i}`}
                type="number"
                inputMode="decimal"
                step="0.25"
                value={editing.value}
                onChange={(e) =>
                  setEditing({ ...editing, value: e.target.value })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyLength(lab.i, editing.value);
                  if (e.key === "Escape") setEditing(null);
                }}
                className="font-mono-data h-11 w-24 border-zinc-700 bg-zinc-900 px-2.5 text-base font-bold"
              />
              <button
                type="button"
                data-testid={`tracer-label-apply-${lab.i}`}
                onClick={() => applyLength(lab.i, editing.value)}
                className="h-11 rounded-md bg-indigo-500 px-3.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                Set
              </button>
              <button
                type="button"
                data-testid={`tracer-label-cancel-${lab.i}`}
                onClick={() => setEditing(null)}
                aria-label="Cancel"
                className="flex h-11 w-9 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-white"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          ) : (
            <button
              key={lab.i}
              type="button"
              disabled={readOnly}
              data-testid={`tracer-label-${lab.i}`}
              onClick={() =>
                setEditing({ index: lab.i, value: String(lab.len) })
              }
              style={{
                left: `${lab.left}%`,
                top: `${lab.top}%`,
                transform: "translate(-50%, -50%)",
              }}
              className={`font-mono-data absolute min-h-[36px] min-w-[54px] rounded-md border border-zinc-600 bg-zinc-950/95 px-2.5 py-1.5 text-sm font-bold text-zinc-100 shadow-lg sm:min-h-[40px] sm:min-w-[60px] sm:px-3 sm:text-base ${
                readOnly
                  ? "cursor-default"
                  : "transition-colors duration-150 hover:border-indigo-400 hover:text-white active:bg-indigo-500/30"
              }`}
            >
              {lab.len}"
            </button>
          ),
        )}
      </div>

      <div className="flex flex-wrap gap-6 text-sm">
        <span>
          <span className="text-xs uppercase tracking-widest text-zinc-500">
            Usable{" "}
          </span>
          <span
            className="font-mono-data text-xl font-bold text-yellow-400"
            data-testid="usable-sqft"
          >
            {sqft} sq ft
          </span>
        </span>
        <span>
          <span className="text-xs uppercase tracking-widest text-zinc-500">
            Full slab{" "}
          </span>
          <span className="font-mono-data text-zinc-300">{fullSqft} sq ft</span>
        </span>
        <span>
          <span className="text-xs uppercase tracking-widest text-zinc-500">
            Corners{" "}
          </span>
          <span className="font-mono-data text-zinc-300">{points.length}</span>
        </span>
      </div>
    </div>
  );
};
